import { PROPFIRMS } from '@/modules/propfirms/data/propfirms';
import {
  AlgoLevel,
  Propfirm,
  PropfirmAlgoPolicy,
} from '@/modules/propfirms/data/types';

/// Les trois usages automatisés que Kara pose sur un compte, dans l'ordre où
/// un trader les rencontre : le terminal (un EA de gestion passe l'ordre que
/// vous décidez), le copieur (vos trades d'un compte à l'autre), l'exécution
/// d'une stratégie (un programme décide seul).
export type AlgoUse = 'tradeManager' | 'copyOwnAccounts' | 'automated';
export const ALGO_USES: readonly AlgoUse[] = [
  'tradeManager',
  'copyOwnAccounts',
  'automated',
];

/// Verdict d'ensemble, du plus ouvert au plus fermé.
export type AlgoTradingVerdict =
  /// Les trois usages passent sans réserve particulière.
  | 'allowed'
  /// Rien n'est interdit, mais au moins un usage a une condition à connaître.
  | 'conditional'
  /// Au moins un usage est interdit, pas tous.
  | 'partial'
  /// Rien ne passe : la firme interdit tout EA.
  | 'forbidden'
  /// Ni interdiction ni condition connue, mais un usage reste sans réponse.
  | 'unknown';

export function algoTradingVerdict(
  policy: PropfirmAlgoPolicy,
): AlgoTradingVerdict {
  const levels: AlgoLevel[] = ALGO_USES.map((use) => policy[use]);
  if (levels.every((level) => level === 'forbidden')) return 'forbidden';
  if (levels.includes('forbidden')) return 'partial';
  if (levels.includes('conditional')) return 'conditional';
  if (levels.includes('unknown')) return 'unknown';
  return 'allowed';
}

/// Rapproche un compte de sa propfirm dans le comparateur.
///
/// Deux jeux de données décrivent les propfirms — celui du comparateur, qui
/// porte les règles, et `config/propfirm-config.ts`, qui porte les logos et
/// les clés de courtier et dont viennent les `propfirmSlug` des comptes. Leurs
/// identifiants ne coïncident pas partout : on tente le slug, puis le nom
/// normalisé. Sans correspondance sûre, on ne renvoie rien — afficher la règle
/// d'une firme voisine serait pire que de ne rien afficher.
const NORMALIZED_NAMES = new Map<string, Propfirm>();
const BY_SLUG = new Map<string, Propfirm>();
for (const firm of PROPFIRMS) {
  BY_SLUG.set(firm.slug, firm);
  NORMALIZED_NAMES.set(normalize(firm.name), firm);
}

function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/// Les quelques firmes que les deux jeux de données nomment différemment.
const SLUG_ALIASES: Record<string, string> = {
  apex: 'apex-trader-funding',
  aquafunded: 'aqua-funded',
  'funding-pips': 'fundingpips',
  'the-5ers': 'the5ers',
};

export function findPropfirmRules(
  slugOrName: string | undefined,
): Propfirm | null {
  if (!slugOrName) return null;
  const direct = BY_SLUG.get(slugOrName);
  if (direct) return direct;
  const aliased = SLUG_ALIASES[slugOrName];
  if (aliased) return BY_SLUG.get(aliased) ?? null;
  return NORMALIZED_NAMES.get(normalize(slugOrName)) ?? null;
}
